import { post } from "@common/api/apiShared.ts";
import { EmailTemplateResponse } from "./CustomizeEmailTextDialog.types";
import { EmailCustomizationTypes } from "../EmailCustomization.types";
import { createElement, useState } from "react";
import { useTranslation } from "@localization/useTranslation.ts";
import { Trans } from "react-i18next";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

// The preview swaps the custom text on every keystroke. Rendering the whole email through
// dangerouslySetInnerHTML each time would rebuild its <img> tags (the customer logo has no fixed height),
// collapsing the header for a frame and making the centered dialog jump. Instead we render the email
// shell once with this stable anchor element and mutate only its contents — see CustomizeEmailTextDialog.
export const previewCustomTextAttribute = "data-custom-text-anchor";

export function useCustomizeEmailTextDialog(emailType: EmailCustomizationTypes, onDialogClose: () => void) {
    const defaultReplaceText = "[CustomText]";
    const snackbar = useDotLegalSnackbar();

    const { emailAddress } = useUserContext();
    const { translateString } = useTranslation();

    const url = `/email-template/${emailType}`;
    const { data: templateData, isLoading } = useDotLegalQuery<EmailTemplateResponse>(url, { refetchOnWindowFocus: false });

    const loadedText = templateData?.currentCustomization ?? "";

    // null until the user edits — until then the preview reads straight from the loaded template.
    // The editor's `value` is bound to the loaded text only (never to this), so the editor's output is
    // not fed back through its `value` prop on each keystroke — that round-trip would run MentionInput's
    // expensive normalize-and-compare sync effect on every key. This holds the latest text for save.
    const [editedPreviewText, setEditedPreviewText] = useState<string | null>(null);
    const previewText = editedPreviewText ?? loadedText;

    const [intervalSelection, setIntervalSelection] = useState<string | null>(null);
    const taskNotificationIntervalInWeeks = resolveInterval(intervalSelection, templateData?.taskNotificationIntervalInWeeks);

    // Stable across keystrokes: the custom text lives in an empty anchor that is filled imperatively,
    // so this string only changes when the template itself loads — React never rebuilds the logo image.
    const getPreviewShellHtml = () => {
        if (!templateData?.htmlTemplate) {
            return "";
        }

        return templateData.htmlTemplate.replace(defaultReplaceText, `<div ${previewCustomTextAttribute}></div>`);
    };

    const saveMutation = useDotLegalMutation(saveAPI);
    const handleSave = async () => {
        await saveMutation.mutateAsync(
            { emailText: previewText, type: emailType, taskNotificationIntervalInWeeks },
            {
                onSuccess: () => {
                    onDialogClose();
                },
            }
        );
    };

    const sendEmailMutation = useDotLegalMutation(sendTestEmailAPI);
    const onSendEmailClick = async () => {
        await sendEmailMutation.mutateAsync(
            { emailText: previewText, type: emailType },
            {
                onSuccess: () => {
                    snackbar.show(createElement(Trans, { i18nKey: "testEmailSent", values: { email: emailAddress } }));
                },
            }
        );
    };

    function onEmailTextChange(value: string) {
        setEditedPreviewText(value);
    }

    function onEmailNotificationIntervalChange(interval: string) {
        setIntervalSelection(interval);
    }

    return {
        editorText: loadedText,
        taskNotificationIntervalInWeeks,
        previewShellHtml: getPreviewShellHtml(),
        previewCustomText: previewText || defaultReplaceText,
        isLoading,
        handleSave,
        isSaving: saveMutation.isLoading,
        translateString,
        sendingEmail: sendEmailMutation.isLoading,
        onSendEmailClick,
        onEmailNotificationIntervalChange,
        onEmailTextChange,
    };

    function resolveInterval(selection: string | null, loaded: number | undefined) {
        if (selection === null) {
            return loaded;
        }

        return selection === "never" ? undefined : Number(selection);
    }

    function saveAPI(data: { emailText: string; type: EmailCustomizationTypes; taskNotificationIntervalInWeeks?: number }) {
        return post<{}>("/email-template/EmailText", data);
    }

    function sendTestEmailAPI(data: { emailText: string; type: EmailCustomizationTypes }) {
        return post<{}>("/email-template/SendTestTextEmail", data);
    }
}
